// Angular
import { Injectable } from '@angular/core';

// Third party
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, catchError, of, switchMap } from 'rxjs';

// Local library
import {
  PusherService,
  ToteConfigurationAPIService,
  TransactionAPIService,
  UserAPIService
} from '@amwager/data-access';

// Local file
import * as AccountActions from './account.actions';
import { fetch } from '@nx/angular';
import { ToteAccountAPIService } from '@amwager/data-access';
import { AccountFacade } from './account.facade';

@Injectable()
export class AccountEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly pusherService: PusherService,
    private readonly transactionAPIService: TransactionAPIService,
    private readonly toteAccountAPIService: ToteAccountAPIService,
    private readonly toteConfigurationAPIService: ToteConfigurationAPIService,
    private readonly userAPIService: UserAPIService,
    private readonly accountFacade: AccountFacade
  ) {}

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.initAccountState),
      switchMap(() => of(AccountActions.loadAccountStateSuccess())),
      catchError((error) => {
        console.error('Error', error);
        return of(AccountActions.loadAccountStateFailure({ error }));
      })
    )
  );

  getAccountBalance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getAccountBalanceAPI),
      fetch({
        id: () => 'getAccountBalance',
        run: () => this.toteAccountAPIService.getToteAccountBalance$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return AccountActions.getAccountBalanceAPISuccess({ successResponse: response });
            })
          )
        ,
        // eslint-disable-next-line no-empty-pattern
        onError: ({}, errorResponse) => AccountActions.getAccountBalanceAPIFailed({ errorResponse }),
      })
    )
  );

  getUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getUserAccountAPI),
      fetch({
        id: () => 'get-user-account',
        run: () => this.userAPIService.account$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return AccountActions.getUserAccountAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.getUserAccountAPIFailed({ errorResponse });
        }
      })
    )
  );

  getUserEprotectInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getUserEprotectInformationAPI),
      fetch({
        id: () => 'get-debit-credit',
        run: () => this.transactionAPIService.eprotectInformation$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              return AccountActions.getUserEprotectInformationAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.getUserEprotectInformationAPIFailed({ errorResponse });
        }
      })
    )
  );

  getAccountStatements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getAccountStatementsAPI),
      fetch({
        id: () => 'get-account-statements',
        run: ({ requests }) => this.toteAccountAPIService.getAccountReportEx$(requests)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return AccountActions.getAccountStatementsAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.getAccountStatementsAPIFailed({ errorResponse });
        }
      })
    )
  );

  selfExcludeOrSuspend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.selfExcludeOrSuspendAPI),
      fetch({
        id: () => 'self-exclude-or-suspend',
        run: ({
          days, years, isExclusion
        }) => this.userAPIService.selfExcludeOrSuspend$(
          years, days, isExclusion
        )
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return AccountActions.selfExcludeOrSuspendAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.selfExcludeOrSuspendAPIFailed({ errorResponse });
        }
      })
    )
  );

  selfExcludeOrSuspendVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.selfExcludeOrSuspendVerificationAPI),
      fetch({
        id: () => 'self-exclude-or-suspend-verification',
        run: ({
          verificationToken
        }) => this.userAPIService.selfExcludeOrSuspendVerification$(
          verificationToken
        )
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return AccountActions.selfExcludeOrSuspendVerificationAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.selfExcludeOrSuspendVerificationAPIFailed({ errorResponse });
        }
      })
    )
  );

  getAccountPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getAccountPreferencesAPI),
      fetch({
        id: () => 'get-account-preferences',
        run: () => this.toteConfigurationAPIService.getToteConfiguration$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                // if ("Model" in response) {
                // if ("AccountPreferences" in response) {
                //   // response = response?.Model;
                //   response = response?.AccountPreferences;
                // }
              } catch (error) {
                console.error("AccountPreferences:API");
              }

              return AccountActions.getAccountPreferencesAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.getAccountPreferencesAPIFailed({ errorResponse });
        }
      })
    )
  );

  upsertAccountPrefenceField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.upsertAccountPrefenceFieldAPI),
      fetch({
        id: () => 'upsert-account-prefences-field',
        run: ({ FieldName, Value }) => this.toteConfigurationAPIService.upsertAccountPrefencesField$(
          {
            Fields: [{fieldName: FieldName, value: Value}]
          }
        )
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   if ("Model" in response) {
              //     response = response?.Model;
              //   }
              // } catch (error) {
              //   console.error("Model:API");
              // }

              if (FieldName === 'AmWagerTheme') {
                return AccountActions.upsertAccountPrefenceFieldAmwagerThemeAPISuccess({
                  fieldName: FieldName,
                  value: Value
                });
              }
              else if (FieldName === 'ListOfFavorites') {
                return AccountActions.upsertAccountPrefenceFieldFavoriteAPISuccess({
                  fieldName: FieldName,
                  value: JSON.parse(Value)
                });
              }
              else if (FieldName === 'WagerLimit' || FieldName === 'IsWagerLimit') {
                return AccountActions.upsertAccountPrefenceFieldWageringLimitsAPISuccess({
                  fieldName: FieldName,
                  value: FieldName === 'WagerLimit' ? Value.toString() : Value
                });
              }
              else {
                return AccountActions.upsertAccountPrefenceFieldAPISuccess({
                  fieldName: FieldName,
                  value: FieldName === 'EventTypeFilter' ? JSON.parse(Value) : Value
                });
              }

            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.upsertAccountPrefenceFieldAPIFailed({ errorResponse });
        }
      })
    )
  );

  getAccountDailyLossLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.getAccountDailyLossLimitAPI),
      fetch({
        id: () => 'get-account-daily-loss-limit',
        run: () => this.toteConfigurationAPIService.lossLimit$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {
              // console.log(response, " AccountDailyLossLimit:API");
              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   // if ("Model" in response) {
              //   if ("AccountDailyLossLimit" in response) {
              //     // response = response?.Model;
              //     response = response?.AccountDailyLossLimit;
              //   }
              // } catch (error) {
              //   console.error("AccountDailyLossLimit:API");
              // }

              return AccountActions.getAccountDailyLossLimitAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.getAccountDailyLossLimitAPIFailed({ errorResponse });
        }
      })
    )
  );

  updateAccountDailyLossLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateAccountDailyLossLimitAPI),
      fetch({
        id: () => 'update-account-daily-loss-limit',
        run: ({ amount }) => this.toteConfigurationAPIService.lossLimitPost$(
          amount.toString()
        )
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   if ("Model" in response) {
              //     response = response?.Model;
              //   }
              // } catch (error) {
              //   console.error("Model:API");
              // }

              return AccountActions.updatetAccountDailyLossLimitAPISuccess({
                amount
              });

            })
          )
        ,
        onError: (param, errorResponse) => {
          return AccountActions.updateAccountDailyLossLimitAPIFailed({ errorResponse });
        }
      })
    )
  );

}
